/**
 * AuthMagicCallback · v2.7.65+E71
 * ══════════════════════════════════════════════════════════════════════
 * Landing page for magic-link URLs (`/auth/magic?token=…&portal=…`).
 * Consumes the token via `POST /api/auth/magic-link/consume`, then
 * routes the caller to the right dashboard based on the returned
 * `portal` field:
 *
 *   driver   → /dashboard      (stores rtg_token + rtg_driver)
 *   admin    → /admin          (stores rtg_admin_token + rtg_admin, handles 2FA inline)
 *   customer → /ride           (stores rtg_customer_token + rtg_customer)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Shield } from "lucide-react";
import { broadcastLogin } from "@/lib/authSync";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AuthMagicCallback() {
  const nav = useNavigate();
  const ranRef = useRef(false);
  const [twofa, setTwofa] = useState({ pendingToken: null, code: "", busy: false });

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const q = new URLSearchParams(window.location.search);
    const token = q.get("token");
    if (!token) {
      toast.error("Missing sign-in token — please request a fresh link");
      nav("/login", { replace: true });
      return;
    }
    api.post("/auth/magic-link/consume", { token })
      .then((r) => {
        const data = r.data || {};
        if (data.requires_2fa) {
          setTwofa({ pendingToken: data.pending_token, code: "", busy: false });
          return;
        }
        const portal = data.portal;
        try {
          if (portal === "driver" && data.driver) {
            localStorage.setItem("rtg_token", data.access_token);
            localStorage.setItem("rtg_driver", JSON.stringify(data.driver));
            broadcastLogin("driver", { token: data.access_token, user: data.driver });
            toast.success(`Welcome ${data.driver?.name?.split(" ")[0] || "driver"}!`);
            window.location.assign("/dashboard");
          } else if (portal === "admin" && data.user) {
            localStorage.setItem("rtg_admin_token", data.access_token);
            localStorage.setItem("rtg_admin", JSON.stringify(data.user));
            broadcastLogin("admin", { token: data.access_token, user: data.user });
            toast.success(`Welcome ${data.user?.name?.split(" ")[0] || "operator"}!`);
            window.location.assign("/admin");
          } else if (portal === "customer" && data.customer) {
            localStorage.setItem("rtg_customer_token", data.access_token);
            localStorage.setItem("rtg_customer", JSON.stringify(data.customer));
            broadcastLogin("customer", { token: data.access_token, user: data.customer });
            toast.success(`Welcome ${data.customer?.name?.split(" ")[0] || "rider"}!`);
            window.location.assign("/ride");
          } else {
            toast.error("Sign-in response missing portal info");
            nav("/login", { replace: true });
          }
        } catch (_e) {
          toast.error("Could not store session — private browsing?");
          nav("/login", { replace: true });
        }
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (detail?.message || "Sign-in link is invalid or expired");
        toast.error(msg, { duration: 6000 });
        nav("/login", { replace: true });
      });
  }, [nav]);

  const submit2fa = async () => {
    if (!twofa.pendingToken || !twofa.code) return;
    setTwofa((t) => ({ ...t, busy: true }));
    try {
      const { data } = await api.post("/admin/login/2fa-verify", {
        pending_token: twofa.pendingToken,
        code: twofa.code.trim(),
      });
      if (!data?.access_token || !data?.user) {
        toast.error("2FA succeeded but response was malformed");
        nav("/admin/login", { replace: true });
        return;
      }
      localStorage.setItem("rtg_admin_token", data.access_token);
      localStorage.setItem("rtg_admin", JSON.stringify(data.user));
      broadcastLogin("admin", { token: data.access_token, user: data.user });
      window.location.assign("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid 2FA code");
      setTwofa((t) => ({ ...t, code: "", busy: false }));
    }
  };

  if (twofa.pendingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-zinc-100 p-6" data-testid="auth-magic-callback-2fa">
        <div className="w-full max-w-sm space-y-4 text-center">
          <Shield className="h-10 w-10 mx-auto text-amber-400"/>
          <div>
            <div className="font-bold text-zinc-100 text-lg">Two-factor authentication</div>
            <div className="text-xs text-zinc-500 mt-1">Enter the 6-digit code from your authenticator app, or a one-time backup code.</div>
          </div>
          <Input
            autoFocus
            data-testid="magic-admin-2fa-code"
            value={twofa.code}
            onChange={(e) => setTwofa((t) => ({ ...t, code: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && submit2fa()}
            placeholder="123456 or backup code"
            className="bg-zinc-950 border-zinc-700 h-12 text-center text-lg tracking-[0.4em]"
          />
          <Button
            data-testid="magic-admin-2fa-submit"
            onClick={submit2fa}
            disabled={twofa.busy || !twofa.code.trim()}
            className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
          >
            {twofa.busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "VERIFY & CONTINUE"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-zinc-100" data-testid="auth-magic-callback">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-400"/>
        <div className="mt-3 text-sm text-zinc-400">Signing you in…</div>
      </div>
    </div>
  );
}
